import { FormMessages } from 'components/ui/form-messages';
import { Button } from 'components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from 'components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from 'components/ui/popover';
import { Input } from 'components/ui/input';
import { useDebounce } from '../../../hooks/use-debounce';
import { X, Loader2 } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import useSWR from 'swr';

export interface AddressType {
  address1: string;
  address2: string;
  formattedAddress: string;
  city: string;
  region: string;
  postalCode: string;
  country: string;
  lat: number;
  lng: number;
}

export const EMPTY_ADDRESS: AddressType = {
  address1: '',
  address2: '',
  formattedAddress: '',
  city: '',
  region: '',
  postalCode: '',
  country: '',
  lat: 0,
  lng: 0,
};

// Direct API keys from env (REACT_APP_ vars are client-accessible in CRA)
const GOOGLE_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';
const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN || '';

// ─── Google Places API (New) ─────────────────────────────────────────

interface GooglePrediction {
  placePrediction: {
    placeId: string;
    place: string;
    text: { text: string };
  };
}

async function googleAutocomplete(input: string): Promise<GooglePrediction[]> {
  const res = await fetch('https://places.googleapis.com/v1/places:autocomplete', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Goog-Api-Key': GOOGLE_KEY,
    },
    body: JSON.stringify({
      input,
      includedPrimaryTypes: ['street_address', 'subpremise', 'route', 'street_number', 'landmark'],
      includedRegionCodes: ['US'],
    }),
  });
  if (!res.ok) throw new Error(`Google autocomplete error: ${res.status}`);
  const data = await res.json();
  return data.suggestions || [];
}

async function googlePlaceDetails(placeId: string): Promise<{ address: AddressType; adrAddress: string }> {
  const res = await fetch(`https://places.googleapis.com/v1/${placeId}`, {
    headers: {
      'X-Goog-Api-Key': GOOGLE_KEY,
      'X-Goog-FieldMask': 'adrFormatAddress,shortFormattedAddress,formattedAddress,location,addressComponents',
      'Content-Type': 'application/json',
    },
  });
  if (!res.ok) throw new Error(`Google place details error: ${res.status}`);
  const data = await res.json();

  const dataFinderRegx = (c: string) => {
    const regx = new RegExp(`<span class="${c}">([^<]+)<\\/span>`);
    const match = data.adrFormatAddress?.match(regx);
    return match ? match[1] : '';
  };

  return {
    address: {
      address1: dataFinderRegx('street-address'),
      address2: '',
      formattedAddress: data.formattedAddress || '',
      city: dataFinderRegx('locality'),
      region: dataFinderRegx('region'),
      postalCode: dataFinderRegx('postal-code'),
      country: dataFinderRegx('country-name'),
      lat: data.location?.latitude || 0,
      lng: data.location?.longitude || 0,
    },
    adrAddress: data.adrFormatAddress || '',
  };
}

// ─── Mapbox Geocoding v5 (via mapboxSdk already loaded by the template) ──────

// Cache features from autocomplete so place details doesn't need a second call
const mapboxFeatureCache = new Map<string, any>();

function getMapboxClient() {
  const w = window as any;
  if (!w.mapboxSdk || !w.mapboxgl?.accessToken) {
    throw new Error('Mapbox SDK not loaded');
  }
  return w.mapboxSdk({ accessToken: w.mapboxgl.accessToken });
}

async function mapboxAutocomplete(input: string): Promise<GooglePrediction[]> {
  const client = getMapboxClient();
  const response = await client.geocoding
    .forwardGeocode({
      query: input,
      limit: 5,
      language: ['en'],
      countries: ['us', 'ca'],
      proximity: 'ip',
    })
    .send();

  const features = response.body.features || [];
  return features.map((feature: any) => {
    mapboxFeatureCache.set(feature.id, feature);
    return {
      placePrediction: {
        placeId: feature.id,
        place: feature.id,
        text: { text: feature.place_name },
      },
    };
  });
}

async function mapboxPlaceDetails(featureId: string): Promise<{ address: AddressType; adrAddress: string }> {
  const feature = mapboxFeatureCache.get(featureId);
  if (!feature) throw new Error('Place not found');

  const center = feature.center || [0, 0]; // [lng, lat]
  const context: any[] = feature.context || [];

  const getCtx = (type: string) => context.find((c: any) => c.id.startsWith(type));

  const address1 = [feature.address, feature.text].filter(Boolean).join(' ');
  const city = getCtx('place')?.text || getCtx('locality')?.text || '';
  const region = getCtx('region')?.short_code?.replace(/^US-|^CA-/, '') || getCtx('region')?.text || '';
  const postalCode = getCtx('postcode')?.text || '';
  const country = getCtx('country')?.text || '';
  const formattedAddress = feature.place_name || '';

  const adrParts: string[] = [];
  if (address1) adrParts.push(`<span class="street-address">${address1}</span>`);
  if (city) adrParts.push(`<span class="locality">${city}</span>`);
  if (region) adrParts.push(`<span class="region">${region}</span>`);
  if (postalCode) adrParts.push(`<span class="postal-code">${postalCode}</span>`);
  if (country) adrParts.push(`<span class="country-name">${country}</span>`);

  return {
    address: {
      address1,
      address2: '',
      formattedAddress,
      city,
      region,
      postalCode,
      country,
      lat: center[1],
      lng: center[0],
    },
    adrAddress: adrParts.join(', '),
  };
}

// ─── Mapbox Search Box API (POI-filtered autocomplete, e.g. schools) ─────

export async function mapboxSearchSuggest(
  input: string,
  categories: string[],
  sessionToken: string,
): Promise<GooglePrediction[]> {
  const params = new URLSearchParams({
    q: input,
    access_token: MAPBOX_TOKEN,
    types: 'poi',
    poi_category: categories.join(','),
    limit: '5',
    language: 'en',
    country: 'US,CA',
    proximity: 'ip',
    session_token: sessionToken,
  });
  const res = await fetch(`https://api.mapbox.com/search/searchbox/v1/suggest?${params}`);
  if (!res.ok) throw new Error(`Mapbox Search suggest error: ${res.status}`);
  const data = await res.json();
  return (data.suggestions || []).map((s: any) => ({
    placePrediction: {
      placeId: s.mapbox_id,
      place: s.mapbox_id,
      text: { text: [s.name, s.place_formatted].filter(Boolean).join(' — ') },
    },
  }));
}

export async function mapboxSearchRetrieve(
  mapboxId: string,
  sessionToken: string,
): Promise<{ address: AddressType; adrAddress: string; poiName?: string }> {
  const params = new URLSearchParams({
    access_token: MAPBOX_TOKEN,
    session_token: sessionToken,
  });
  const res = await fetch(
    `https://api.mapbox.com/search/searchbox/v1/retrieve/${mapboxId}?${params}`,
  );
  if (!res.ok) throw new Error(`Mapbox Search retrieve error: ${res.status}`);
  const data = await res.json();
  const feature = data.features?.[0];
  if (!feature) throw new Error('No feature returned');

  const props = feature.properties || {};
  const coords = props.coordinates || {};
  const ctx = props.context || {};

  const address1 = props.address || '';
  const city = ctx.place?.name || ctx.locality?.name || '';
  const region = ctx.region?.region_code || ctx.region?.name || '';
  const postalCode = ctx.postcode?.name || '';
  const country = ctx.country?.name || '';
  const formattedAddress = props.full_address || props.name || '';

  const adrParts: string[] = [];
  if (address1) adrParts.push(`<span class="street-address">${address1}</span>`);
  if (city) adrParts.push(`<span class="locality">${city}</span>`);
  if (region) adrParts.push(`<span class="region">${region}</span>`);
  if (postalCode) adrParts.push(`<span class="postal-code">${postalCode}</span>`);
  if (country) adrParts.push(`<span class="country-name">${country}</span>`);

  return {
    address: {
      address1,
      address2: '',
      formattedAddress,
      city,
      region,
      postalCode,
      country,
      lat: coords.latitude || 0,
      lng: coords.longitude || 0,
    },
    adrAddress: adrParts.join(', '),
    poiName: props.name || undefined,
  };
}

// ─── Unified fetchers (pick provider based on which key exists) ──────

async function fetchAutocomplete(
  input: string,
  poiCategories?: string[],
  sessionToken?: string,
): Promise<GooglePrediction[]> {
  if (poiCategories?.length && MAPBOX_TOKEN && sessionToken) {
    return mapboxSearchSuggest(input, poiCategories, sessionToken);
  }
  if (GOOGLE_KEY) return googleAutocomplete(input);
  if (MAPBOX_TOKEN) return mapboxAutocomplete(input);
  return [];
}

async function fetchPlaceDetails(
  placeId: string,
  poiCategories?: string[],
  sessionToken?: string,
): Promise<{ address: AddressType; adrAddress: string; poiName?: string }> {
  if (poiCategories?.length && MAPBOX_TOKEN && sessionToken) {
    return mapboxSearchRetrieve(placeId, sessionToken);
  }
  if (GOOGLE_KEY) return googlePlaceDetails(placeId);
  if (MAPBOX_TOKEN) return mapboxPlaceDetails(placeId);
  throw new Error('No map provider API key configured');
}

// ─── Component ───────────────────────────────────────────────────────

interface AddressAutoCompleteProps {
  address: AddressType;
  setAddress: (address: AddressType) => void;
  searchInput: string;
  setSearchInput: (searchInput: string) => void;
  dialogTitle: string;
  showInlineError?: boolean;
  placeholder?: string;
  poiCategories?: string[];
  onPoiName?: (name: string) => void;
}

export default function AddressAutoComplete(props: AddressAutoCompleteProps) {
  const {
    address,
    setAddress,
    dialogTitle,
    showInlineError = true,
    searchInput,
    setSearchInput,
    placeholder,
    poiCategories,
    onPoiName,
  } = props;

  const [selectedPlaceId, setSelectedPlaceId] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const sessionTokenRef = useRef(crypto.randomUUID());

  const sessionToken = poiCategories?.length ? sessionTokenRef.current : undefined;

  const { data, isLoading } = useSWR(
    selectedPlaceId === '' ? null : ['place-details', selectedPlaceId],
    () => fetchPlaceDetails(selectedPlaceId, poiCategories, sessionToken),
    { revalidateOnFocus: false },
  );

  const adrAddress = data?.adrAddress;

  useEffect(() => {
    if (data?.address) {
      setAddress(data.address);
      if (data.poiName && onPoiName) {
        onPoiName(data.poiName);
      }
    }
  }, [data, setAddress, onPoiName]);

  return (
    <>
      {selectedPlaceId !== '' || address.formattedAddress ? (
        <div className="flex items-center gap-2">
          <Input value={address?.formattedAddress} readOnly />
          <Button
            type="reset"
            onClick={() => {
              setSelectedPlaceId('');
              setAddress(EMPTY_ADDRESS);
              setSearchInput('');
              sessionTokenRef.current = crypto.randomUUID();
            }}
            size="icon"
            variant="outline"
            className="shrink-0"
          >
            <X className="size-4" />
          </Button>
        </div>
      ) : (
        <AddressAutoCompleteInput
          searchInput={searchInput}
          setSearchInput={setSearchInput}
          selectedPlaceId={selectedPlaceId}
          setSelectedPlaceId={setSelectedPlaceId}
          setIsOpenDialog={setIsOpen}
          showInlineError={showInlineError}
          placeholder={placeholder}
          poiCategories={poiCategories}
          sessionToken={sessionToken}
        />
      )}
    </>
  );
}

interface CommonProps {
  selectedPlaceId: string;
  setSelectedPlaceId: (placeId: string) => void;
  setIsOpenDialog: (isOpen: boolean) => void;
  showInlineError?: boolean;
  searchInput: string;
  setSearchInput: (searchInput: string) => void;
  placeholder?: string;
  poiCategories?: string[];
  sessionToken?: string;
}

function AddressAutoCompleteInput(props: CommonProps) {
  const {
    setSelectedPlaceId,
    selectedPlaceId,
    setIsOpenDialog,
    showInlineError,
    searchInput,
    setSearchInput,
    placeholder,
    poiCategories,
    sessionToken,
  } = props;

  const [isOpen, setIsOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const debouncedSearchInput = useDebounce(searchInput, 500);

  const { data, isLoading } = useSWR(
    debouncedSearchInput ? ['autocomplete', debouncedSearchInput, poiCategories] : null,
    () => fetchAutocomplete(debouncedSearchInput, poiCategories, sessionToken),
  );

  const predictions: GooglePrediction[] = data || [];

  // Open the popover whenever there's input text
  useEffect(() => {
    setIsOpen(searchInput.length > 0);
  }, [searchInput]);

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <div className="flex w-full items-center justify-between rounded-lg border bg-background ring-offset-background text-sm focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-0">
          <input
            ref={inputRef}
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            onFocus={() => searchInput.length > 0 && setIsOpen(true)}
            placeholder={placeholder || 'Enter address'}
            className="w-full h-10 px-3 py-2 rounded-lg outline-none text-sm bg-transparent"
          />
        </div>
      </PopoverTrigger>
      {searchInput !== '' && !isOpen && !selectedPlaceId && showInlineError && (
        <FormMessages
          type="error"
          className="pt-1 text-sm"
          messages={['Select a valid address from the list']}
        />
      )}
      <PopoverContent
        className="p-0"
        align="start"
        sideOffset={4}
        onOpenAutoFocus={e => e.preventDefault()}
        style={{ minWidth: 'var(--radix-popover-trigger-width)' }}
      >
        <Command shouldFilter={false}>
          <CommandList className="max-h-[240px] text-sm">
            <CommandGroup>
              {isLoading ? (
                <div className="h-28 flex items-center justify-center">
                  <Loader2 className="size-6 animate-spin" />
                </div>
              ) : (
                predictions.map((prediction) => (
                  <CommandItem
                    value={prediction.placePrediction.text.text}
                    onSelect={() => {
                      setSearchInput('');
                      setSelectedPlaceId(prediction.placePrediction.place);
                      setIsOpenDialog(true);
                      setIsOpen(false);
                    }}
                    key={prediction.placePrediction.placeId}
                    className="cursor-pointer text-sm"
                  >
                    {prediction.placePrediction.text.text}
                  </CommandItem>
                ))
              )}
            </CommandGroup>
            {!isLoading && predictions.length === 0 && (
              <CommandEmpty>
                <div className="py-4 flex items-center justify-center text-sm">
                  No address found
                </div>
              </CommandEmpty>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
